// File: frontend/src/components/entries/MatchTitleOfEntries.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MatchModel } from "../../models";
import { getTeamPagePathFromParts } from "../../models/TeamModel";
import "./MatchTitleOfEntries.css";
import { MatchStatusEnum } from "../../models/enums/MatchStatusEnum";
import { FaInfoCircle } from "react-icons/fa";
import ReactCountryFlag from "react-country-flag";
import { MatchInfoModal } from "./MatchInfoModal";

interface MatchTitleOfEntriesProps {
  match: MatchModel | null;
}

/**
 * Entry-list header for a match. Deliberately carries the identity of the match
 * only — home team, score, away team — on a single fixed-height line.
 *
 * Competition, round, date, time, venue, extra-time/penalty breakdown and the
 * referee crew used to be stacked underneath as four separate rows; with a full
 * referee crew that pushed the entries themselves off the first screen. They now
 * live in MatchInfoModal, opened from the "maç bilgisi" button. Anything added
 * later (line-ups, cards, goals) goes into that modal, so this header keeps its
 * height forever.
 */
export const MatchTitleOfEntries: React.FC<MatchTitleOfEntriesProps> = ({
  match,
}) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  if (!match) return null;

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

  // Render team logo if available, otherwise fall back to country flag
  const renderTeamLogo = (logoUrl: string | undefined, countryCode: string, teamName: string) => {
    if (logoUrl) {
      return (
        <img
          src={`${API_URL}${logoUrl}`}
          alt={teamName}
          className="match-title__team-logo"
          // A dead logo URL used to leave a broken-image placeholder next to the
          // team name — hide the element instead of showing the browser's icon
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      );
    }
    return (
      <ReactCountryFlag
        countryCode={countryCode}
        svg
        className="match-title__team-logo match-title__team-logo--flag"
      />
    );
  };

  // Team page links for the header — null when the match has no resolvable
  // club slug (e.g. an old match row with a corrupted team relation), in which
  // case the name falls back to plain (non-clickable) text further down.
  const homeTeamPath = match.homeTeamClubSlug
    ? getTeamPagePathFromParts(match.homeTeamClubSlug, match.sportType, match.matchGender, match.homeTeamCode)
    : null;
  const awayTeamPath = match.awayTeamClubSlug
    ? getTeamPagePathFromParts(match.awayTeamClubSlug, match.sportType, match.matchGender, match.awayTeamCode)
    : null;

  // POSTPONED / CANCELLED matches never carry a real score — the stored 0-0 is
  // only a default value, which used to render as a goalless draw in the header.
  const isPostponed = match.status === MatchStatusEnum.POSTPONED;
  const isCancelled = match.status === MatchStatusEnum.CANCELLED;
  const hasNoScore  = isPostponed || isCancelled;

  // "- : -" only while SCHEDULED with no score entered yet
  const scoreText = isPostponed
    ? t("match_status_short_postponed")
    : isCancelled
    ? t("match_status_short_cancelled")
    : match.status === MatchStatusEnum.SCHEDULED &&
      match.homeScore === 0 &&
      match.awayScore === 0
      ? "- : -"
      : `${match.homeScore} - ${match.awayScore}`;

  // Extra CSS classes applied to the score element for the status codes
  const scoreStatusClass = isPostponed
    ? " match-title__score--status match-title__score--postponed"
    : isCancelled
    ? " match-title__score--status match-title__score--cancelled"
    : "";

  // Single muted line under the score, e.g. "UZ 2 - 1 · PEN 4 - 2".
  // Rendered only when there is something to show: reserving an empty slot
  // pushed the score number visually above the team names, because the score
  // block was vertically centred including that empty line. The fixed
  // min-height on .match-title__score-block keeps the header height stable
  // instead.
  // Never render the extra-time / penalty line for a match that has no score
  const extraParts: string[] = [];
  if (!hasNoScore && match.homeScoreEt != null && match.awayScoreEt != null) {
    extraParts.push(`${t("match_score_et_short")} ${match.homeScoreEt} - ${match.awayScoreEt}`);
  }
  if (!hasNoScore && match.homeScorePen != null && match.awayScorePen != null) {
    extraParts.push(`${t("match_score_pen_short")} ${match.homeScorePen} - ${match.awayScorePen}`);
  }

  return (
    <>
      <div className="match-title">
        {/* Three-column grid: the score column has a fixed width, so it stays on the
            optical centre of the card no matter how long either team name is.
            The info button is positioned absolutely for the same reason — as a grid
            item it would push the score off centre. */}
        <div className="match-title__grid">
          {/* title on the wrapper as well as the name: on narrow screens the
              name is hidden and only the logo is shown, and the tooltip has to
              come from the element that is still visible.
              Rendered as a Link to the team page when homeTeamPath is resolvable;
              falls back to a plain div (no navigation) for older/corrupted rows. */}
          {homeTeamPath ? (
            <Link
              to={homeTeamPath}
              className="match-title__team match-title__team--home match-title__team-link"
              title={match.homeTeam}
            >
              <span className="match-title__team-name" title={match.homeTeam}>{match.homeTeam}</span>
              {renderTeamLogo(match.homeTeamLogoUrl, match.homeTeamCountry, match.homeTeam)}
            </Link>
          ) : (
            <div className="match-title__team match-title__team--home" title={match.homeTeam}>
              <span className="match-title__team-name" title={match.homeTeam}>{match.homeTeam}</span>
              {renderTeamLogo(match.homeTeamLogoUrl, match.homeTeamCountry, match.homeTeam)}
            </div>
          )}

          <div className="match-title__score-block">
            <strong
              className={`match-title__score${scoreStatusClass}`}
              title={isPostponed ? t("match_status_postponed") : isCancelled ? t("match_status_cancelled") : undefined}
            >
              {scoreText}
            </strong>
            {extraParts.length > 0 && (
              <span className="match-title__subscore">{extraParts.join(" · ")}</span>
            )}
          </div>

          {awayTeamPath ? (
            <Link
              to={awayTeamPath}
              className="match-title__team match-title__team--away match-title__team-link"
              title={match.awayTeam}
            >
              {renderTeamLogo(match.awayTeamLogoUrl, match.awayTeamCountry, match.awayTeam)}
              <span className="match-title__team-name" title={match.awayTeam}>{match.awayTeam}</span>
            </Link>
          ) : (
            <div className="match-title__team match-title__team--away" title={match.awayTeam}>
              {renderTeamLogo(match.awayTeamLogoUrl, match.awayTeamCountry, match.awayTeam)}
              <span className="match-title__team-name" title={match.awayTeam}>{match.awayTeam}</span>
            </div>
          )}
        </div>

        <button
          type="button"
          className="match-title__info-btn"
          onClick={() => setInfoOpen(true)}
          title={t("match_info_button")}
        >
          <FaInfoCircle className="match-title__info-icon" />
          <span className="match-title__info-label">{t("match_info_button")}</span>
        </button>
      </div>

      {infoOpen && <MatchInfoModal match={match} onClose={() => setInfoOpen(false)} />}
    </>
  );
};